import * as React from 'react';
import Page from "../../Component/Page/Page";
import TreeMaps from "../MapPage/TreeMaps";
import PublicPageMapPopup from "./PublicPageMapPopup";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { getPublicCountByCountry } from "../../Repository/TreeRepository";
import { GeoJsonFile } from "tree-wallet-client";
import { GlobalContext } from "../../Contexts/GlobalContext";
import { getHighlightedCountries } from "../../Repository/TreeRepository";

export default function PublicPage() {
  const { updateTrees, counterRef } = useContext(GlobalContext);
  const [loading, setLoading] = useState<boolean>(true);
  const [selectedCountryId, setSelectedCountryId] = useState<string|undefined>(undefined);
  const [geoJsonFiles, setGeoJsonFiles] = useState<GeoJsonFile[]>([]);
  const [highlightedCountries, setHighlightedCountries] = useState<any[]|undefined>(undefined);
  const [countrySpecificData, setCountrySpecificData] = useState<any>([]);
  const [countryDataLoading, setCountryDataLoading] = useState<boolean>(false);

  // The country request in flight, reachable without waiting for a React commit: leaving a country has
  // to abort its trees download on the spot, and the selection change that ends up doing it is batched
  // behind the map cleanup (see `handleCountryDeselect`)
  // `cancel` also flips the effect's `cancelled` flag, so an aborted request can never apply its response
  const countryRequestRef = useRef<{ cancel: () => void } | null>(null);
  

  // Initial data loading - load highlighted countries
  useEffect(() => {
    let count = 0;
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    counterRef?.current?.setTrees(count);
    
    (async () => {
      try {
        const apiResponse = await getHighlightedCountries();

        const countries = Array.isArray(apiResponse?.data)
          ? apiResponse.data.map((c: { country_id: string; trees_count: string }) => ({
              country_id: c.country_id,
              trees_count: Number(c.trees_count),
            }))
          : [];
        setHighlightedCountries(countries);
        count = countries.reduce((acc: number, country: { trees_count: number }) => acc + country.trees_count, 0);
        
      } catch (err) {
        console.error("Error loading initial data:", err);
      }
      setLoading(false);
      updateTrees && updateTrees(count, undefined, true);
    })();
  }, []);

  useEffect(() => {
    if (!selectedCountryId) {
      setCountrySpecificData([]);
      setGeoJsonFiles([]);
      setCountryDataLoading(false);
      return;
    }

    // `cancelled` drops the response of a country that is no longer the selected one: the
    // payloads are heavy (Uganda: ~16.7 MB, ~4-5s), so a late response used to be applied to the
    // country the user had switched to in the meantime.
    let cancelled = false;

    // Dropping the response is not enough on Back: the download of a heavy country kept running
    // to completion while the map was already back on the world view. The request is aborted
    // with it - `Http.request` forwards this signal to `fetch`. Aborting is the normal end of a
    // country the user left, so it must not be logged as a failure.
    const controller = new AbortController();

    // `handleCountryDeselect` calls to stop running tree download on the spot, 
    // instead of waiting for this effect's cleanup (which only runs after the map cleanup)
    countryRequestRef.current = {
      cancel: () => {
        cancelled = true;
        controller.abort();
      }
    };

    setCountryDataLoading(true);

    (async () => {
      try {
        const countryData = await getPublicCountByCountry({
          params: {
            country_code: selectedCountryId
          },
          controller
        });
        if (!cancelled) {
          setCountrySpecificData(countryData);
        }
      } catch (err) {
        if (!cancelled) {
          console.log(err, "Error fetching country data:");
        }
      } finally {
        // Reports that no feature can arrive anymore. This is what ends the map's wait for the
        // country data (that wait has no timeout on purpose) and it removes the loading banner,
        // so a failed request cannot leave either of them hanging.
        if (!cancelled) {
          setCountryDataLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
      countryRequestRef.current = null;
    };
  }, [selectedCountryId]);
  

  const handleCountrySelect = (iso: string) => {
    setSelectedCountryId(iso);
  };

  const handleCountryDeselect = () => {
    // Stops the download of the country being left asap, 
    // rather than through the effect cleanup following country selection change (see `countryRequestRef`)
    countryRequestRef.current?.cancel();
    setSelectedCountryId(undefined);
    setCountrySpecificData([]);
    setGeoJsonFiles([]);
  };

  const handleCountrySpecificData = useCallback((data: any[]) => setCountrySpecificData(data), []);

  return (
    <Page isPublic={true} fullWidth fullHeight hideMenu={false} title={''} loading={loading}>
        <TreeMaps
        template={PublicPageMapPopup}
        initialPosition={{
          coordinates: [18.110757900028602, 29.72442689243057],
          zoomLevel: 2.2
        }}
        public={true}
        fullHeight
        language={"latin"}
        geoJsonFiles={geoJsonFiles}
        highlightedCountries={highlightedCountries}
        selectedCountryId={selectedCountryId} 
        setCountrySpecificData={handleCountrySpecificData}
        countryDataLoading={countryDataLoading}
        onCountrySelect={handleCountrySelect}
        onCountryDeselect={handleCountryDeselect}
        countrySpecificData={countrySpecificData}
      />
    </Page>
  );
}
